import { createFileRoute } from '@tanstack/react-router'
import { ImagePlus, Star, StarOff, Upload } from 'lucide-react'
import { useRef, useState } from 'react'

import { Product, ProductImage } from '@/api/admin-products-types'
import { useAttachProductImage, useDeleteProductImage, useSetPrimaryProductImage } from '@/api/api-mutations'
import type { Asset } from '@/api/common-types'
import { useListProductImages } from '@/api/react-query'
import { AssetLibraryPicker } from '@/components/asset-library-picker'
import { AssetImage } from '@/components/base/asset-image'
import { Button } from '@/components/base/button'
import { FileDropZone, isAcceptedImageFile } from '@/components/base/file-drop-zone'
import { FocusModal } from '@/components/base/focus-modal'
import { Heading } from '@/components/base/heading'
import { IconButton } from '@/components/base/icon-button'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { Tooltip } from '@/components/base/tooltip'
import { RouteModal } from '@/components/route-modal'
import { useAssetUpload } from '@/hooks/use-asset-upload'
import { useI18n } from '@/i18n/i18n-react'
import { ArrowDownTray } from '@/icons/arrow-down-tray'
import { Trash } from '@/icons/trash'
import { clx } from '@/utils/clx'

const ACCEPTED_FORMATS = 'image/jpeg,image/jpg,image/png,image/webp,image/gif'

export const Route = createFileRoute('/_authenticated/_general/products/$productUuid/media')({
	component: RouteComponent
})

function RouteComponent() {
	const { productUuid } = Route.useParams()

	return (
		<RouteModal requiresData loader={async api => await api.getProduct({ path: { productUuid } })}>
			{product => <ProductMediaComponent product={product} />}
		</RouteModal>
	)
}

function ProductMediaComponent({ product }: { product: Product }) {
	const { L } = useI18n()
	const { productUuid } = Route.useParams()
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [pendingFiles, setPendingFiles] = useState<File[]>([])
	const [libraryPickerOpen, setLibraryPickerOpen] = useState(false)

	const { data: images, isLoading } = useListProductImages({ productUuid }, { initialData: product.images })

	const attach = useAttachProductImage(false, productUuid)
	const remove = useDeleteProductImage(false, productUuid)
	const setPrimary = useSetPrimaryProductImage(false, productUuid)
	const { upload, isUploading, progress } = useAssetUpload({ category: 'PRODUCT' })

	const handleSelect = () => fileInputRef.current?.click()

	const handleFiles = async (files: FileList | null) => {
		if (!files || files.length === 0) return
		const fileArray = Array.from(files).filter(isAcceptedImageFile)
		if (fileArray.length === 0) {
			toast.error(L.General.InvalidFileType(files[0]?.name ?? '', 'jpeg, png, webp, gif'))
			if (fileInputRef.current) fileInputRef.current.value = ''
			return
		}
		setPendingFiles(fileArray)
		try {
			for (const file of fileArray) {
				const { asset } = await upload(file)
				await attach({
					path: { productUuid },
					body: { assetUuid: asset.uuid }
				})
			}
			toast.success(L.Product.Media.UploadSuccessToast({ count: fileArray.length }))
		} catch (e: any) {
			toast.error(e?.message ?? L.Product.Media.UploadFailedToast())
		} finally {
			setPendingFiles([])
			if (fileInputRef.current) fileInputRef.current.value = ''
		}
	}

	const handleAttachFromLibrary = async (asset: Asset) => {
		try {
			await attach({ path: { productUuid }, body: { assetUuid: asset.uuid } })
			toast.success(L.Product.Media.AddImageSuccessToast())
		} catch (e: any) {
			toast.error(e?.message ?? L.Product.Media.UploadFailedToast())
		}
	}

	const handleDelete = async (image: ProductImage) => {
		try {
			await remove({ path: { productUuid, imageUuid: image.uuid } })
			toast.success(L.Product.Media.DeleteSuccessToast())
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	const handleSetPrimary = async (image: ProductImage) => {
		if (image.isPrimary) return
		try {
			await setPrimary({ path: { productUuid, imageUuid: image.uuid } })
			toast.success(L.Product.Media.SetPrimarySuccessToast())
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	const handleDownload = (image: ProductImage) => {
		// Prefer the ORIGINAL variant for downloads (matches the user's uploaded file).
		const original = image.asset.variants.find(v => v.type === 'ORIGINAL')
		const url = original?.url ?? image.asset.variants[0]?.url
		if (!url) return
		const a = document.createElement('a')
		a.href = url
		a.download = image.asset.filename
		a.target = '_blank'
		a.click()
	}

	const showEmpty = !isLoading && (images?.length ?? 0) === 0 && pendingFiles.length === 0

	return (
		<>
			<FocusModal.Header>
				<div className='flex w-full items-center justify-between gap-x-2'>
					<Heading level='h2'>{L.Product.Media.Title()}</Heading>
					<div className='flex items-center gap-x-2'>
						<Button
							size='small'
							variant='secondary'
							onClick={() => setLibraryPickerOpen(true)}
							disabled={isUploading}
						>
							<ImagePlus className='mr-2 h-4 w-4' />
							{L.Product.Media.ChooseFromLibrary()}
						</Button>
						<Button size='small' variant='secondary' onClick={handleSelect} disabled={isUploading}>
							<Upload className='mr-2 h-4 w-4' />
							{L.Product.Media.Upload()}
						</Button>
					</div>
				</div>
			</FocusModal.Header>
			<FocusModal.Body className='flex flex-col gap-y-4 overflow-y-auto p-6'>
				<input
					ref={fileInputRef}
					type='file'
					accept={ACCEPTED_FORMATS}
					multiple
					hidden
					onChange={e => handleFiles(e.target.files)}
				/>
				<FileDropZone
					className='flex min-h-full flex-1 flex-col'
					disabled={isUploading}
					hint={L.General.DropImages()}
					onFiles={handleFiles}
				>
					{showEmpty ? (
						<EmptyState onUpload={handleSelect} disabled={isUploading} />
					) : (
						<div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
							{(images ?? []).map(image => (
								<ImageCard
									key={image.uuid}
									image={image}
									onDelete={() => handleDelete(image)}
									onSetPrimary={() => handleSetPrimary(image)}
									onDownload={() => handleDownload(image)}
								/>
							))}
							{pendingFiles.map((file, index) => (
								<UploadingCard key={`pending-${index}-${file.name}`} file={file} progress={progress} />
							))}
						</div>
					)}
				</FileDropZone>
			</FocusModal.Body>
			<AssetLibraryPicker
				open={libraryPickerOpen}
				onOpenChange={setLibraryPickerOpen}
				attachedAssetUuids={new Set((images ?? []).map(image => image.asset.uuid))}
				onSelect={handleAttachFromLibrary}
				title={L.Product.Media.LibraryPickerTitle()}
				emptyLabel={L.Product.Media.LibraryPickerEmpty()}
				allAttachedLabel={L.Product.Media.LibraryPickerAllAttached()}
			/>
		</>
	)
}

function EmptyState({ onUpload, disabled }: { onUpload: () => void; disabled: boolean }) {
	const { L } = useI18n()
	return (
		<div className='flex flex-1 flex-col items-center justify-center gap-y-4 rounded-lg border border-dashed border-ui-border-strong bg-ui-bg-subtle p-12'>
			<div className='flex flex-col items-center'>
				<Text size='small' leading='compact' weight='plus' className='text-ui-fg-subtle'>
					{L.Product.Media.EmptyState.Header()}
				</Text>
				<Text size='small' className='text-ui-fg-muted'>
					{L.Product.Media.EmptyState.Description()}
				</Text>
			</div>
			<Button size='small' variant='secondary' onClick={onUpload} disabled={disabled}>
				<Upload className='mr-2 h-4 w-4' />
				{L.Product.Media.EmptyState.Action()}
			</Button>
		</div>
	)
}

function ImageCard({
	image,
	onDelete,
	onSetPrimary,
	onDownload
}: {
	image: ProductImage
	onDelete: () => void
	onSetPrimary: () => void
	onDownload: () => void
}) {
	const { L } = useI18n()
	const isProcessing = image.asset.status !== 'READY'

	return (
		<div className='group relative flex flex-col rounded-lg border border-ui-border-base bg-ui-bg-base'>
			<div className='relative'>
				<AssetImage
					asset={image.asset}
					className={clx('aspect-square w-full rounded-t-lg', isProcessing && 'opacity-60')}
				/>
				{image.isPrimary && (
					<div className='absolute left-2 top-2 rounded-md bg-ui-bg-base px-2 py-1 shadow-borders-base'>
						<Text size='xsmall' weight='plus'>
							{L.Product.Media.PrimaryBadge()}
						</Text>
					</div>
				)}
				{isProcessing && (
					<div className='absolute inset-0 flex items-center justify-center'>
						<Text size='xsmall' className='bg-ui-bg-base/90 rounded-md px-2 py-1 text-ui-fg-subtle'>
							{L.Product.Media.ProcessingBadge()}
						</Text>
					</div>
				)}
			</div>
			<div className='flex items-center justify-between gap-x-2 border-t border-ui-border-base px-3 py-2'>
				<Text size='xsmall' className='truncate text-ui-fg-subtle' title={image.asset.filename}>
					{image.asset.filename}
				</Text>
				<div className='flex items-center gap-x-1'>
					<Tooltip
						content={
							image.isPrimary ? L.Product.Media.AlreadyPrimaryLabel() : L.Product.Media.SetPrimaryLabel()
						}
					>
						<IconButton size='small' type='button' onClick={onSetPrimary} disabled={image.isPrimary}>
							{image.isPrimary ? (
								<Star className='h-4 w-4 fill-current' />
							) : (
								<StarOff className='h-4 w-4' />
							)}
							<span className='sr-only'>{L.Product.Media.SetPrimaryLabel()}</span>
						</IconButton>
					</Tooltip>
					<Tooltip content={L.Product.Media.DownloadImageLabel()}>
						<IconButton size='small' type='button' onClick={onDownload}>
							<ArrowDownTray />
							<span className='sr-only'>{L.Product.Media.DownloadImageLabel()}</span>
						</IconButton>
					</Tooltip>
					<Tooltip content={L.Product.Media.DeleteImageLabel()}>
						<IconButton size='small' type='button' onClick={onDelete}>
							<Trash />
							<span className='sr-only'>{L.Product.Media.DeleteImageLabel()}</span>
						</IconButton>
					</Tooltip>
				</div>
			</div>
		</div>
	)
}

function UploadingCard({ file, progress }: { file: File; progress: number }) {
	const { L } = useI18n()
	return (
		<div className='relative flex aspect-square flex-col items-center justify-center rounded-lg border border-dashed border-ui-border-strong bg-ui-bg-subtle p-4'>
			<Text size='small' weight='plus' className='truncate text-ui-fg-subtle'>
				{file.name}
			</Text>
			<div className='mt-3 h-1 w-full overflow-hidden rounded-full bg-ui-bg-component'>
				<div
					className='h-full bg-ui-fg-interactive transition-all'
					style={{ width: `${Math.max(progress, 5)}%` }}
				/>
			</div>
			<Text size='xsmall' className='mt-2 text-ui-fg-muted'>
				{L.Product.Media.UploadingLabel({ percent: progress })}
			</Text>
		</div>
	)
}
